import React from 'react';
import {
  Datagrid,
  DateField,
  ReferenceManyField,
  SelectField,
  TextField,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import hasAccess from '../../utilities/hasAccess';
import Pagination from '../layout/Pagination';
import NoResults from '../layout/NoResults';
import actions, { Actions } from '../../constants/actions';
import { listViewDatagridStyle } from '../../constants/style/datagridStyles';
import PermissionMissing from '../utils/PermissionMissing';
import resourceNames from '../../constants/resourceNames';
import { deviceTypesChoices } from '../../constants/deviceTypes';

const HARDWARE_ID_TOOLTIP = 'Hardware ID: A unique identifier tied to the physical device used to initiate this transaction.';
const ELECTRONIC_ID_TOOLTIP = 'Electronic ID: A personal digital identity credential (e.g. national eID) linked to the account holder.';

const deviceTypeTooltip = (
  <Box>
    <Box mb={1}>
      Device type is the type of identifier associated with the device used for a transaction.
    </Box>
    <Box mb={0.5}>
      {HARDWARE_ID_TOOLTIP}
    </Box>
    <Box>
      {ELECTRONIC_ID_TOOLTIP}
    </Box>
  </Box>
);

const DeviceUsages = () => {
  const { permissions } = usePermissions<Actions[]>();
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Card variant="outlined">
      <CardHeader
        title="Device usage"
        action={(
          <Tooltip title={deviceTypeTooltip} arrow>
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        )}
      />
      <Divider />
      {!hasAccess(permissions, actions.DEVICE_USAGE_LIST) ? (
        <CardContent>
          <Box display="flex" alignItems="center" width="100%" flexDirection="column">
            <PermissionMissing />
          </Box>
        </CardContent>
      ) : (
        <CardContent>
          <ReferenceManyField
            reference={resourceNames.DEVICE_USAGES}
            target="entityId"
            perPage={15}
            pagination={<Pagination disableEmptyText />}
          >
            <Datagrid sx={listViewDatagridStyle} bulkActionButtons={false} empty={<NoResults />}>
              <TextField sortable={false} source="deviceId" label="Device ID" />
              <SelectField sortable={false} source="deviceType" label="Device Type" choices={deviceTypesChoices} />
              <DateField sortable={false} source="firstTimeUsed" label="First time used" showTime />
              <DateField sortable={false} source="lastTimeUsed" label="Last time used" showTime />
            </Datagrid>
          </ReferenceManyField>
        </CardContent>
      )}
    </Card>
  );
};

export default DeviceUsages;
